import { formatDate } from "@/lib/format";
// web/src/pages/payments/PaymentsListPage.tsx
//
// I-7 — Payments list page.
//
// Canonical chrome (Plan IV Task 2):
// - search: client-side filter across payment ID / invoice ID / account ID /
//   payment type code / status (backend /api/payments has no text-search param).
// - filterChips: single-select view (All / Pending / Posted today / Reversed)
//   that drives backend `status=` + `from=` params.
// - itemCount: wired from data?.totalCount.
//
// "Record Payment" CTA opens RecordPaymentModal (no invoice context).
// URL ?openModal=true auto-opens the modal on mount.
import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listPayments,
  type PaymentRecordSummaryDto,
} from "@/lib/invoices-payments";
import { PaymentMethodBadge } from "@/components/payment-method-badge";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn, type SortDirection } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/app/auth-context";
import { RecordPaymentModal } from "./RecordPaymentModal";
import { EditPaymentModal } from "./EditPaymentModal";

// ---------------------------------------------------------------------------
// View chips — single-select, drives backend status + from filters
// ---------------------------------------------------------------------------

export type PaymentStatusCode = "Pending" | "Posted" | "Reversed" | "Failed";

type ViewChipId = "all" | "pending" | "postedToday" | "reversed";

const VIEW_CHIP_OPTIONS: ReadonlyArray<{ id: ViewChipId; label: string }> = [
  { id: "all",         label: "All" },
  { id: "pending",     label: "Pending" },
  { id: "postedToday", label: "Posted today" },
  { id: "reversed",    label: "Reversed" },
];

interface ViewParams {
  status?: PaymentStatusCode[];
  from?: string;
}

function applyView(view: ViewChipId): ViewParams {
  switch (view) {
    case "pending":
      return { status: ["Pending"] };
    case "postedToday": {
      const start = new Date();
      start.setUTCHours(0, 0, 0, 0);
      return { status: ["Posted"], from: start.toISOString() };
    }
    case "reversed":
      return { status: ["Reversed"] };
    case "all":
    default:
      return {};
  }
}

// ---------------------------------------------------------------------------
// Currency formatter
// ---------------------------------------------------------------------------

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

// ---------------------------------------------------------------------------
// PaymentsListPage component interface
// ---------------------------------------------------------------------------

export interface PaymentsListPageProps {
  /** Reserved for future use (e.g. pre-filter by account). */
  initialAccountId?: number;
}

// ---------------------------------------------------------------------------
// PaymentsListPage
// ---------------------------------------------------------------------------

export function PaymentsListPage({ initialAccountId }: PaymentsListPageProps = {}) {
  const qc = useQueryClient();
  const { permissions } = useAuth();
  const canWrite = permissions.has("payments:write");
  const [searchParams] = useSearchParams();
  const [searchText, setSearchText] = useState("");
  const [selectedView, setSelectedView] = useState<ViewChipId>("all");
  const [page, setPage] = useState(0);
  const [modalOpen, setModalOpen] = useState(() => searchParams.get("openModal") === "true");
  const [editingPayment, setEditingPayment] = useState<PaymentRecordSummaryDto | null>(null);

  // Auto-open modal if ?openModal=true is present on mount.
  useEffect(() => {
    if (searchParams.get("openModal") === "true") {
      setModalOpen(true);
    }
  }, [searchParams]);

  const params = useMemo(() => {
    const view = applyView(selectedView);
    return {
      status: view.status,
      from: view.from,
      accountId: initialAccountId,
      page,
      pageSize: 50,
    };
  }, [selectedView, initialAccountId, page]);

  const { data, isLoading } = useQuery({
    queryKey: ["payments", params],
    queryFn: () => listPayments(params),
  });

  // Client-side text search across primary fields.
  // /api/payments has no `q=` param (see PaymentsEndpoints.cs); current page only.
  const filteredRows = useMemo(() => {
    const items = data?.items ?? [];
    const q = searchText.trim().toLowerCase();
    if (!q) return items;
    return items.filter((r) => {
      return (
        String(r.paymentRecordId).includes(q) ||
        (r.invoiceId != null && String(r.invoiceId).includes(q)) ||
        (r.accountId != null && String(r.accountId).includes(q)) ||
        r.paymentTypeCode.toLowerCase().includes(q) ||
        r.status.toLowerCase().includes(q)
      );
    });
  }, [data, searchText]);

  // Sort state — client-side only; listPayments has no `sort` param.
  // Sort applies to the (already client-filtered) current page rows.
  const [sort, setSort] = useState<{ columnId: string; direction: SortDirection } | null>(null);

  const visibleRows = useMemo(() => {
    if (!sort) return filteredRows;
    const dir = sort.direction === "asc" ? 1 : -1;
    const valueFor = (r: PaymentRecordSummaryDto): string | number | null => {
      switch (sort.columnId) {
        case "id":          return r.paymentRecordId;
        case "date":        return r.transactionDate;
        case "account":     return r.accountId ?? null;
        case "invoice":     return r.invoiceId ?? null;
        case "type":        return r.paymentTypeCode;
        case "amount":      return r.tranAmount;
        case "status":      return r.status;
        case "allocations": return r.allocationCount;
        default:            return null;
      }
    };
    return [...filteredRows].sort((a, b) => {
      const va = valueFor(a);
      const vb = valueFor(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [filteredRows, sort]);

  // ---------------------------------------------------------------------------
  // Columns
  // ---------------------------------------------------------------------------

  const cols: DataGridColumn<PaymentRecordSummaryDto>[] = [
    {
      id: "id",
      header: "Payment ID",
      sortable: true,
      accessor: (r) => String(r.paymentRecordId),
    },
    {
      id: "date",
      header: "Transaction date",
      sortable: true,
      accessor: (r) => formatDate(r.transactionDate),
    },
    {
      id: "account",
      header: "Account",
      sortable: true,
      accessor: (r) => (r.accountId != null ? `#${r.accountId}` : "—"),
    },
    {
      id: "invoice",
      header: "Invoice",
      sortable: true,
      accessor: (r) => (r.invoiceId != null ? `#${r.invoiceId}` : "—"),
    },
    {
      id: "type",
      header: "Type",
      sortable: true,
      accessor: (r) => <PaymentMethodBadge code={r.paymentTypeCode} />,
    },
    {
      id: "amount",
      header: "Amount",
      sortable: true,
      accessor: (r) => fmt.format(r.tranAmount),
      align: "right",
    },
    {
      id: "status",
      header: "Status",
      sortable: true,
      accessor: (r) => r.status,
    },
    {
      id: "allocations",
      header: "Allocations",
      sortable: true,
      accessor: (r) => String(r.allocationCount),
      align: "right",
    },
    {
      id: "actions",
      header: "",
      accessor: (r) =>
        canWrite && r.status === "Posted" ? (
          <div className="flex gap-2 justify-end">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditingPayment(r)}
            >
              Edit
            </Button>
          </div>
        ) : null,
    },
  ];

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <>
      <ListPage
        title="Payments"
        primaryAction={
          <IntentButton onClick={() => setModalOpen(true)}>
            + Record Payment
          </IntentButton>
        }
        search={{
          value: searchText,
          onChange: (v) => { setSearchText(v); setPage(0); },
          placeholder: "Search payments…",
        }}
        filterChips={{
          options: VIEW_CHIP_OPTIONS,
          value: selectedView,
          onChange: (next) => { setSelectedView(next); setPage(0); },
          "aria-label": "Payment view filter",
        }}
      >
        <DataGrid
          rows={visibleRows}
          columns={cols}
          rowKey={(r) => r.paymentRecordId}
          totalRows={data?.totalCount ?? 0}
          pageIndex={page}
          pageSize={50}
          loading={isLoading}
          emptyHeadline="No payments match these filters."
          onPageChange={setPage}
          onSortChange={setSort}
        />
      </ListPage>

      <RecordPaymentModal
        open={modalOpen}
        onOpenChange={setModalOpen}
      />

      {editingPayment && (
        <EditPaymentModal
          open={editingPayment !== null}
          onOpenChange={(o) => { if (!o) setEditingPayment(null); }}
          paymentRecordId={editingPayment.paymentRecordId}
          onSuccess={() => {
            void qc.invalidateQueries({ queryKey: ["payments"] });
          }}
        />
      )}
    </>
  );
}
